import {
    EzClockerException,
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzArray,
    EzJson,
    EzPromise,
    EzUrl
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    EzRegistrationState,
    EzContextEventName,
    EzClockerAccountType
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';

import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
import { EzEventEngine } from '/ezlibrary/ez-event-engine.js';
import { EzHttpHelper } from '/public/javascript/common/ezclocker-http-helper.js';
import { EzNavigation } from '/public/javascript/common/ezclocker-navigation-helper.js';
import { EzEmployerOption } from '/ezlibrary/EzEmployerOption.js';

/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) DO NOT ADD ANY ADDITIONAL CONSTANTS TO EzOptionsService - Always use EzEmployerOption instead!
    2) Do not import: import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
       EzDateTime will cause a circual dependency. Instead, check for
       globalThis.ezApi.ezclocker?.ezDateTime before using and when not available
       fall back to using moment.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

/**
 * @class
 * @extends {EzClass}
 * @description
 * Provides ezClocker option api services
 * TODO: Rename to EzEmployerOptionsInternalApiClient
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzOptionsService } from '/public/javascript/services/ezclocker-options.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton instance ready for use when the below evaluates to true:
 *     globalThis.ezApi.ezclocker?.[EzOptionsService.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Listen to onReady event:
 *     EzClass.ezWaitReady(
 *         EzOptionsService.ezEventNames.onReady,
 *         {listening_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton Instance Reference:
 *     Inside this class...: EzOptionsService.ezInstance
 *     Outside this class..: globalThis.ezApi.ezclocker.ezOptionsService
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzOptionsService extends EzClass {
    /**
     * @private @field
     * Stores the optionKey=value for all loaded/known employer specific options.
     * Default: EzObject.EMPTY
     * @type {object}
     */
    #ezEmployerOptions = EzObject.EMPTY;
    /**
     * @public @property @getter
     * Gets the optionKey=value for all loaded/known employer specific options.
     * @returns {object}
     */
    get ezEmployerOptions() {
        return this.#ezEmployerOptions;
    }
    /**
     * @public @property @setter
     * Sets the optionKey=value for all loaded/known employer specific options.
     * @param {object} employerOptions
     * Default: EzObject.EMPTY
     */
    set ezEmployerOptions(employerOptions = EzObject.EMPTY) {
        this.#ezEmployerOptions = EzObject.assignOrDefault(
            employerOptions,
            EzObject.EMPTY);
    }

    /**
     * @private @field
     * Stores the optionKey=value for all loaded/known employee specific options.
     * @type {object}
     */
    #ezEmployeeOptions = EzObject.EMPTY;
    /**
     * @public @property @getter
     * Gets the optionKey=value for all loaded/known employee specific options.
     * @returns {object}
     */
    get ezEmployeeOptions() {
        return this.#ezEmployeeOptions;
    }
    /**
     * @public @property @setter
     * Sets the optionKey=value for all loaded/known employee specific options.
     * @param {object} employerOptions
     * Default: EzObject.EMPTY
     */
    set ezEmployeeOptions(employeeOptions = EzObject.EMPTY) {
        this.#ezEmployeeOptions = EzObject.assignOrDefault(
            employeeOptions,
            EzObject.EMPTY);
    }

    /**
     * @public @readonly @property
     * Gets the default schedule week object:
     * @returns {object}
     * Example return object:
     *  {
     *      scheduleWeekMoment: {now date time start of day as type moment},
     *      scheduleWeekIso: {now date time start of day as iso string}
     *  }
     */
    get ezDefaultScheduleWeek() {
        const scheduleWeekMoment = EzObject.isValid(globalThis.ezApi.ezclocker?.ezDateTime)
            ? globalThis.ezApi.ezclocker.ezDateTime.ezNowWithStartOfDay()
            : moment();

        return {
            scheduleWeekMoment,
            scheduleWeekIso: EzObject.isValid(globalThis.ezApi.ezclocker?.ezDateTime)
                ? globalThis.ezApi.ezclocker.ezDateTime.ezToIso(scheduleWeekMoment)
                : scheduleWeekMoment.format()
        };
    }

    /**
     * @public @method
     * Gets the default schedule week object.
     *
     * @returns {object}
     * Example return object:
     *  {
     *      scheduleWeekMoment: {now date time start of day as type moment},
     *      scheduleWeekIso: {now date time start of day as iso string}
     *  }
     * @deprecated
     * Migrate to: Property globalThis.ezApi.ezclocker.ezOptionsService.ezDefaultScheduleWeek;
     */
    ezGetDefaultScheduleWeek() {
        return EzOptionsService.ezInstance.ezDefaultScheduleWeek;
    }

    /**
     * @public @method
     * Gets the default selected period moment and iso valus.
     *
     * @returns {object}
     * Example result:
     *  {
     *      periodStart: {now date time start of day minus 7 days as type moment},
     *      periodEnd: {now date time end of day plus 7 days},
     *      startMoment: {now date time start of day minus 7 days as type moment},
     *      endMoment: {now date time end of day plus 7 days as type moment},
     *      startIso: {now date time start of day minus 7 days as an iso string},
     *      endIso: {now date time end of day plus 7 days as an iso string}
     *  }
     */
    get ezDefaultPeriod() {
        const startMoment = EzObject.isValid(globalThis.ezApi.ezclocker?.ezDateTime)
            ? globalThis.ezApi.ezclocker.ezDateTime.ezMinusDays(
                globalThis.ezApi.ezclocker.ezDateTime.ezNowWithStartOfDay(),
                EzNumber.SEVEN)
            : moment().subtract(EzNumber.SEVEN, 'days');

        const endMoment = EzObject.isValid(globalThis.ezApi.ezclocker?.ezDateTime)
            ? globalThis.ezApi.ezclocker.ezDateTime.ezAddDays(
                globalThis.ezApi.ezclocker.ezDateTime.ezNowWithEndOfDay(),
                EzNumber.SEVEN)
            : moment().add(EzNumber.SEVEN, 'days');

        return {
            startMoment,
            endMoment,
            periodStart: startMoment,
            periodEnd: endMoment,
            startIso: EzObject.isValid(globalThis.ezApi.ezclocker?.ezDateTime)
                ? globalThis.ezApi.ezclocker.ezDateTime.ezToIso(startMoment)
                : startMoment.format(),
            endIso: EzObject.isValid(globalThis.ezApi.ezclocker?.ezDateTime)
                ? globalThis.ezApi.ezclocker.ezDateTime.ezToIso(startMoment)
                : startMoment.format()
            };
    }

    /**
     * @public @method
     * Gets the default selected period moment and iso valus.
     *
     * @returns {object}
     * Example result:
     *  {
     *      periodStart: {now date time start of day minus 7 days as type moment},
     *      periodEnd: {now date time end of day plus 7 days},
     *      startMoment: {now date time start of day minus 7 days as type moment},
     *      endMoment: {now date time end of day plus 7 days as type moment},
     *      startIso: {now date time start of day minus 7 days as an iso string},
     *      endIso: {now date time end of day plus 7 days as an iso string}
     *  }
     *
     * @deprecated
     * Migrate to: Property globalThis.ezApi.ezclocker.ezOptionsService.ezDefaultPeriod;
     */
    ezGetDefaultPeriod() {
        return EzOptionsService.ezInstance.ezDefaultPeriod;
    }

    /**
     * @public @method
     * Initializes EzOptionsService
     * @returns {EzOptionsService}
     */
    ezInit() {
        // Register want events
        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            EzContextEventName.onActiveEmployerReady,
            EzOptionsService.ezApiName,
            EzOptionsService.ezInstance.ezSetDefaultOptionValues);

        return EzOptionsService.ezInstance;
    }


    /**
     * @public @method
     * Sets the default values for all known options
     */
    ezSetDefaultOptionValues() {
        globalThis.ezApi.ezclocker.ezOptionsService.ezSetDefaultEmployeeOptionValues();

        globalThis.ezApi.ezclocker.ezOptionsService.ezSetDefaultEmployerOptionValues();
    }

    /**
     * @public @method
     * Caches ALL the available options for an employer
     * @param {Number} employerId
     * @returns {Promise.resolve}
     */
    ezCacheEmployerOptions(employerId) {
        return EzPromise.asyncAction(
            (resolve) => {
                globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
                    globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiUrl(`employer/${employerId}/options`, 'v1'))
                    .then(
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
                    .then(
                        (response) => {
                            if (0 === response.errorCode && EzArray.hasLength(response.entities)) {
                                response.entities.forEach((employerOption) => {
                                    if (EzObject.isValid(employerOption) && EzString.hasLength(employerOption.optionName)) {
                                        EzOptionsService.ezInstance.ezEmployerOptions[employerOption.optionName] = !EzString.hasLength(
                                            employerOption.optionValue)
                                            ? EzString.EMPTY
                                                : employerOption.optionValue;
                                    }
                                });
                            }

                            return resolve(EzOptionsService.ezInstance.ezEmployerOptions);
                        },
                        (eResponse) => {
                            globalThis.ezApi.ezclocker.ezLogger.error(
                                EzString.em`
                                    Failed to cache employer options.
                                    ${globalThis.ezApi.ezclocker.ezDialog.ezExtractErrorMessageFromResponse(eResponse)},
                                    ${EzJson.toJson(eResponse)}`);

                            return resolve(EzOptionsService.ezInstance.ezEmployerOptions);
                        });
            });
    }

    /**
     * @public @method
     * Saves all the cached employer options in one bulk operation.
     * @param {number} employerId
     *
     * @returns {Promise}
     */
    ezSaveEmployerOptions(employerId) {
        const responses = [];

        return EzPromise.promise(
            (resolve, reject) => {
                const employerOptionNames = EzObject.ezKeys(EzOptionsService.ezInstance.ezEmployerOptions);

                let hasErrorResponse = false;

                return EzPromise.asyncAction(
                    (finished) => {
                        let promiseResultCount = 0;

                        for (const employerOptionName of employerOptionNames) {
                            EzOptionsService.ezInstance.ezPersistEmployerOption(
                                employerId,
                                employerOptionName,
                                EzOptionsService.ezInstance.ezEmployerOptions[employerOptionName])
                                .then(
                                    (response) => {
                                        responses.push(response);

                                        promiseResultCount++;

                                        if (employerOptionNames.length === promiseResultCount) {
                                            finished();
                                        }
                                    },
                                    (eResponse) => {
                                        hasErrorResponse = true;

                                        responses.push(
                                            globalThis.ezApi.ezclocker.ezServices.ezBuildErrorResponse(
                                                eResponse.errorCode,
                                                eResponse.message));

                                        promiseResultCount++;

                                        if (employerOptionNames.length === promiseResultCount) {
                                            finished();
                                        }
                                    });
                        }
                })
                .then(
                    () => EzBoolean.isFalse(hasErrorResponse)
                        ? resolve(responses)
                        : reject(responses));
            });
    }

    /**
     * @public @method
     * Saves the employer option value
     *
     * @param {number} employerId
     * @param {string} optionName
     * @param {string} optionValue
     *
     * @returns {Promise}
     *
     * @deprecated
     * Migrate to globalThis.ezApi.ezclocker.ezOptionsService.ezPersistEmployerOption()
     */
    saveEmployerOption(employerId, optionName, optionValue) {
        return EzOptionsService.ezInstance.ezPersistEmployerOption(
            employerId,
            optionName,
            optionValue);
    }

    /**
     * @public @method
     * Persists the employer option to the backend db.
     *
     * @param {number} employerId
     * @param {string} ezEmployerOption
     * A constant from EzEmployerOption
     * @param {*} optionValue
     *
     * @returns {Promise}
     */
    ezPersistEmployerOption(employerId, ezEmployerOption, optionValue) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezPersistEmployerOption);
        }
        if (!EzString.hasLength(ezEmployerOption)) {
            throw new EzBadParamException(
                'ezEmployerOption',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezPersistEmployerOption);
        }
        if (!EzObject.isValid(optionValue)) {
            throw new EzBadParamException(
                'optionValue',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezPersistEmployerOption);
        }
        if (!EzString.isString(optionValue)) {
            optionValue = optionValue.toString();
        }

        return EzPromise.promise(
            (resolve, reject) => {
                globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
                    globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiServiceUrl(`employer/options/${ezEmployerOption}`, 'v2'),
                    EzJson.toJson({
                        'optionName': ezEmployerOption,
                        'optionValue': EzOptionsService.ezInstance.ezNormalizeOptionValue(optionValue)
                    }))
                    .then(
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
                    .then(
                        (response) => {
                            if (EzObject.isValid(response?.employerOption)) {
                                // Cache the updated option
                                EzOptionsService.ezInstance.ezEmployerOptions[response.employerOption.optionName] = response.employerOption?.optionValue;

                                return resolve(response);
                            }

                            return reject(response);
                        },
                        reject);
            });
    }

    /**
     * @public @method
     * Returns true if the value is 1, true, or yes.
     * Returns false if the value is null, empty string, 0, false, or no.
     * Returns false if none of the previous conditions matched (error state)
     *
     * @param {boolean|string} optionValue
     * @param {undefined|null|boolean|*} defaultValue
     * Default: true
     *
     * @returns {boolean}
     */
    ezOptionValueToBoolean(optionValue, defaultValue = true) {
        if (EzBoolean.isBoolean(optionValue)) {
            return optionValue;
        }

        if (!EzString.hasLength(optionValue)) {
            return defaultValue;
        }

        switch (optionValue.toUpperCase()) {
            case EzEmployerOption.EZOPTION_VALUE_TRUE:
            case 'TRUE':
            case 'T':
            case 'YES':
            case 'Y':
            case 'OK':
                return true;
            case EzEmployerOption.EZOPTION_VALUE_FALSE:
            case 'FALSE':
            case 'F':
            case 'NO':
            case 'N':
            case 'CANCEL':
                return false;
            default:
                return defaultValue;
        }
    }

    /**
     * @public @method
     * Converts a boolean value to it's optionValue string
     * true returns '1'
     * false returns '0'
     *
     * @param {Boolean} booleanValue
     *
     * @returns {String}
     */
    ezBooleanToOptionValue(booleanValue) {
        if (!EzBoolean.isBoolean(booleanValue)) {
            return EzEmployerOption.EZOPTION_VALUE_FALSE;
        }

        return EzBoolean.isTrue(booleanValue)
            ? EzEmployerOption.EZOPTION_VALUE_TRUE
            : EzEmployerOption.EZOPTION_VALUE_FALSE;
    }

    /**
     * @public @method
     * Returns the number (via parseInt) if the value is a string number.
     * Returns the default value if the value is NOT a string number.
     * Returns NaN if defaultValue is not provided and the optionValue is not a number.
     *
     * @param {String} optionValue
     * @param {Number|NaN|null} defaultValue
     *
     * @returns {Number|NaN}
     */
    ezOptionValueToNumber(optionValue, defaultValue) {
        defaultValue = !EzNumber.isNumber(defaultValue) || isNaN(defaultValue)
            ? NaN
            : defaultValue;

        if (EzNumber.isNumber(optionValue)) {
            return optionValue;
        }

        if (!EzString.hasLength(optionValue)) {
            return defaultValue;
        }

        if ('NaN' === optionValue) {
            return NaN;
        }

        const result = EzNumber.asNumber(optionValue);

        if (isNaN(result)) {
            return defaultValue;
        }

        return result;
    }

    /**
     * @public @method
     * Converts the provided numberValue to the optionValue string.
     * Null or undefined numberValue will return 'NaN'
     *
     * @returns {String}
     */
    ezNumberToOptionValue(numberValue) {
        if (!EzNumber.isNumber(numberValue)) {
            return 'NaN';
        }

        return numberValue.toString();
    }

    /**
     * @public @method
     * Returns the iso date time of the optionValue.
     * If the valid is not a valid ISO, then the defaultValue is returned.
     * If the default value is not provided, null is returned.
     *
     * @param {String} optionValue
     * @param {Number} defaultValue
     *
     * @returns {moment}
     *
     * @deprecated
     * Migrate to globalThis.ezApi.ezclocker.ezOptionsService.ezOptionValueToMoment
     */
    ezOptionValueToIsoDateTime(optionValue, defaultValue) {
        defaultValue = EzObject.isValid(defaultValue)
            ? defaultValue
            : null;

        if (!EzString.hasLength(optionValue)) {
            return defaultValue;
        }

        if (!globalThis.ezApi.ezclocker.ezDateTime.ezIsValidIsoDateTime(optionValue)) {
            return defaultValue;
        }

        return globalThis.ezApi.ezclocker.ezDateTime.ezFromIso(
            optionValue,
            globalThis.ezApi.ezclocker.ezDateTime.activeTimeZone);
    }

    /**
     * @public @method
     * Returns the iso date time of the optionValue.
     * If the valid is not a valid ISO, then the defaultValue is returned.
     * If the default value is not provided, null is returned.
     *
     * @param {String} aOptionValue
     * @param {Number} aDefaultValue
     *
     * @returns {moment}
     */
    ezOptionValueToMoment(aOptionValue, aDefaultValue) {
        aDefaultValue = EzObject.isValid(aDefaultValue)
            ? aDefaultValue
            : null;

        if (!EzString.hasLength(aOptionValue)) {
            throw new EzBadParamException(
                'aOptionValue',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezOptionValueToMoment);
        }

        if (!globalThis.ezApi.ezclocker.ezDateTime.ezIsValidIsoString(aOptionValue)) {
            return aDefaultValue;
        }

        return globalThis.ezApi.ezclocker.ezDateTime.ezCreateFromIso(aOptionValue);
    }

    /**
     * @public @method
     * Converts the provided momentValue to the optionValue string (an ISO date time)
     * If the momentValue is null or undefined, a blank string is returned.
     *
     * @returns {String}
     */
    ezMomentToOptionValue(momentValue) {
        if (!EzObject.isValid(momentValue)) {
            return EzString.EMPTY;
        }

        return globalThis.ezApi.ezclocker.ezDateTime.ezToIsoDateTime(momentValue);
    }

    /**
     * @public @method
     * Sets the default employee options values
     */
    ezSetDefaultEmployeeOptionValues() {
        EzOptionsService.ezInstance.ezEmployeeOptions[EzEmployerOption.EZOPTION_EMPLOYEE_SELECTED_PERIOD] = EzOptionsService.ezInstance.ezDefaultPeriod;

        EzOptionsService.ezInstance.ezEmployeeOptions[EzEmployerOption.EZOPTION_EMPLOYEE_LAST_SELECTED_SCHEDULE_WEEK] =
            globalThis.ezApi.ezclocker.ezDateTime.ezNowWithStartOfDay();
    }

    /**
     * @public @method
     * Sets the default employer option values
     */
    ezSetDefaultEmployerOptionValues() {
        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_STARTDATE] =
            globalThis.ezApi.ezclocker.ezDateTime.ezToIsoDateTime(
                globalThis.ezApi.ezclocker.ezDateTime.ezMinusDays(
                    globalThis.ezApi.ezclocker.ezDateTime.ezNowWithStartOfDay(),
                    EzNumber.SEVEN));

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD_ENDDATE] =
            globalThis.ezApi.ezclocker.ezDateTime.ezToIsoDateTime(
                globalThis.ezApi.ezclocker.ezDateTime.ezAddDays(
                    globalThis.ezApi.ezclocker.ezDateTime.ezNowWithEndOfDay(),
                    EzNumber.SEVEN));

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD] = EzOptionsService.ezInstance.ezDefaultPeriod;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYER_LAST_SELECTED_SCHEDULE_WEEK] =
            globalThis.ezApi.ezclocker.ezDateTime.ezNowWithStartOfDayIso();

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYER_DASHBOARD_PREFERRED_TIMEZONE] =
            EzString.stringWithLengthOrDefault(
                globalThis.ezApi.ezclocker?.ezDateTime?.activeTimeZone,
                moment.tz.guess(true));

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_EMPLOYEE_ID] = EzEmployerOption.EZOPTION_VALUE_EMPTY;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_PUSH_NOTIFICATIONS_ENABLED] = EzEmployerOption.EZOPTION_VALUE_TRUE;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_REQUIRE_LOCATION_FOR_CLOCKINOUT] = EzEmployerOption.EZOPTION_VALUE_FALSE;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYER_DISABLE_TIME_ENTRY_EDITING] = EzEmployerOption.EZOPTION_VALUE_FALSE;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_ALLOW_RECORDING_OF_UNPAID_BREAKS] = EzEmployerOption.EZOPTION_VALUE_TRUE;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_ALLOW_AUTOMATIC_BREAKS] = EzEmployerOption.EZOPTION_VALUE_FALSE;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.AUTO_BREAK_WORK_HOURS_OPTION] = EzNumber.FOUR.toString();

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.AUTO_BREAK_WORK_MINUTES_OPTION] = EzNumber.FIFTEEN.toString();

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_USE_24HOURTIME_IN_EXPORT_REPORTS] =
            EzEmployerOption.EZOPTION_VALUE_FALSE;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_USE_DECIMAL_TOTALS_IN_EXPORT_REPORTS] = EzEmployerOption.EZOPTION_VALUE_FALSE;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYEE_USE_24HOURTIME_IN_EXPORT_REPORTS] =
            EzEmployerOption.EZOPTION_VALUE_FALSE;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYER_JOBS_REPORT_GROUP_BY_JOB] = EzEmployerOption.EZOPTION_VALUE_TRUE;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYER_SCHEDULE_START_DAY] = EzNumber.ZERO.toString();

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_CALCULATE_OVERTIME_IN_TIME_SHEET_EXPORTS] =
            EzEmployerOption.EZOPTION_VALUE_FALSE;

        EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_CALCULATE_WEEKLY_OVERTIME_AFTER_HOURS] = '40';
    }

    /**
     * @public @method
     * Normalizes the value of an option to bring the values to a consistent format. Some were stored in a
     * Normalizes format for earlier relases.
     *
     * @param {string|null} ezOptionValueToBoolean
     *
     * @returns {string}
     */
    ezNormalizeOptionValue(optionValue) {
        if (EzString.isString(optionValue)) {
            if ('true' === optionValue.toLowerCase()) {
                return EzEmployerOption.EZOPTION_VALUE_TRUE;
            }

            if ('false' === optionValue.toLowerCase()) {
                return EzEmployerOption.EZOPTION_VALUE_FALSE;
            }

            return optionValue;
        }

        if (EzBoolean.isBoolean(optionValue)) {
            return optionValue
                ? EzEmployerOption.EZOPTION_VALUE_TRUE
                : EzEmployerOption.EZOPTION_VALUE_FALSE;
        }

        if (!EzObject.isValid(optionValue)) {
            return EzString.EMPTY;
        }

        return optionValue;
    }

    /**
     * @public @method
     * Gets an employer option or the defaultValue if the option did not exist.
     *
     * @param {string} optionName
     * @param {*} defaultValue
     *
     * @returns {*}
     */
    ezGetEmployerOption(optionName, defaultValue) {
        if (!globalThis.ezApi.ezHasOwnProperty(EzOptionsService.ezInstance.ezEmployerOptions, optionName)) {
            EzOptionsService.ezInstance.ezEmployerOptions[optionName] = EzOptionsService.ezInstance.ezNormalizeOptionValue(defaultValue);

            return EzOptionsService.ezInstance.ezEmployerOptions[optionName];
        }

        return EzOptionsService.ezInstance.ezEmployerOptions[optionName];
    }

    /**
     * @public @method
     * Sets an employer option's value. Returns the optionValue that was set.
     *
     * @param {string} optionName
     * @param {string} optionValue
     *
     * @returns {string}
     */
    ezSetEmployerOption(optionName, optionValue) {
        if (!EzString.hasLength(optionName)) {
            throw new EzBadParamException(
                'optionName',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezSetEmployerOption);
        }

        EzOptionsService.ezInstance.ezEmployerOptions[optionName] = globalThis.ezApi.ezclocker.ezOptionsService.ezNormalizeOptionValue(optionValue);

        EzOptionsService.ezInstance.ezPersistEmployerOption(
            globalThis.ezApi.ezclocker.ezClockerContext.ezGetActiveEmployer().id,
            optionName,
            EzOptionsService.ezInstance.ezEmployerOptions[optionName])
            .then(
                globalThis.ezApi.ezIgnoreResolve,
                (eResponse) => globalThis.ezApi.ezclocker.ezLogger.error(
                    EzString.em`
                        Failed to persist employer option "${optionName}=${optionValue}".
                        Error: ${EzJson.toJson(eResponse, 2)}`));

        return EzOptionsService.ezInstance.ezEmployerOptions[optionName];
    }

    /**
     * @public @method
     * Gets an employee's option. Returns the default value if the employee's option did not exist.
     *
     * @param {string} optionName
     * @param {string} defaultValue
     *
     * @return {string}
     */
    ezGetEmployeeOption(optionName, defaultValue) {
        if (!globalThis.ezApi.ezHasOwnProperty(EzOptionsService.ezInstance.ezEmployerOptions, optionName)) {
            EzOptionsService.ezInstance.ezEmployeeOptions[optionName] = EzOptionsService.ezInstance.ezNormalizeOptionValue(defaultValue);

            return EzOptionsService.ezInstance.ezEmployeeOptions[optionName];
        }

        return EzOptionsService.ezInstance.ezNormalizeOptionValue(EzOptionsService.ezInstance.ezEmployerOptions[optionName]);
    }

    /**
     * @public @method
     * Sets an employee option to the provided value. Returns the optionValue that was set.
     *
     * @param {string} optionName
     * @param {*} optionValue
     *
     * @returns {*}
     */
    ezSetEmployeeOption(optionName, optionValue) {
        if (!EzString.hasLength(optionName)) {
            throw new EzBadParamException(
                'optionName',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezSetEmployerOption);
        }

        optionValue = EzOptionsService.ezInstance.ezNormalizeOptionValue(optionValue);

        EzOptionsService.ezInstance.ezEmployeeOptions[optionName] = optionValue;

        return optionValue;
    }

    /**
     * @public @method
     * Reads the employer's last selected period from the stored options.
     *
     * @param {number} employerId
     *
     * @returns {Promise.resolve}
     *
     * @deprecated
     * Migrate to globalThis.ezApi.ezclocker.ezClockerContext functionality
     */
    readEmployerSelectedPeriod(employerId) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.readEmployerSelectedPeriod);
        }

        return EzPromise.asyncAction(
            (resolve) => EzOptionsService.ezInstance.ezReadEmployerOption(
            employerId,
                EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD,
                EzJson.toJson(EzOptionsService.ezInstance.ezDefaultPeriod))
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
            .then(
                (response) => {
                        const employerPeriod = globalThis.ezApi.ezFromJson(response.employerOption.optionValue);

                    return resolve({
                            periodStart: !EzObject.isValid(employerPeriod) || globalThis.ezApi.ezIsNotEmptyString(employerPeriod.startDateIso8601)
                            ? globalThis.ezApi.ezclocker.ezDateTime.ezCreateFromIso(employerPeriod.startDateIso8601)
                                : EzOptionsService.ezInstance.ezDefaultPeriod.startMoment,
                            periodEnd: !EzObject.isValid(employerPeriod) || globalThis.ezApi.ezIsNotEmptyString(employerPeriod.endDateIso8601)
                            ? globalThis.ezApi.ezclocker.ezDateTime.ezCreateFromIso(employerPeriod.endDateIso8601)
                                : EzOptionsService.ezInstance.ezDefaultPeriod.endMoment
                    });
                },
                (eResponse) => {
                    globalThis.ezApi.ezclocker.ezLogger.error(
                            `Failed to read the employer's elected period. Error: ${EzJson.toJson(eResponse)}`);

                        return resolve(EzOptionsService.ezInstance.ezDefaultPeriod);
                }));
    }

    /**
     * @public @method
     * Reads an employer option (as a string) for the provided optionKey. Returns the defaultValue
     * if unable to read the option.
     *
     * @param {Number} employerId
     * @param {String} optionKey
     * @param {String} defaultValue
     *
     * @returns {Promise.resolve}
     */
    ezReadEmployerOptionValue(employerId, optionKey, defaultValue) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezReadEmployerOptionValue);
        }
        if (!EzString.hasLength(optionKey)) {
            throw new EzBadParamException(
                'optionKey',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezReadEmployerOptionValue);
        }

        if (!EzString.isString(defaultValue)) {
            defaultValue = EzString.EMPTY;
        }

        return EzPromise.asyncAction(
            (resolve) => {
                EzOptionsService.ezInstance.ezReadEmployerOption(employerId, optionKey, defaultValue)
                    .then(
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                        globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
                    .then(
                        (response) => resolve(response.optionValue),
                        (eResponse) => {
                            globalThis.ezApi.ezclocker.ezLogger.error(
                                EzString.em`
                                    Failed to read employer option value for optionName=${optionKey}.
                                    Error: ${EzJson.toJson(eResponse)}`);

                            return resolve(defaultValue);
                        });
            });
    }

    /**
     * @public @method
     * Saves the employers last selected period
     *
     * @param {number} employerId
     * @param {moment} startMoment
     * @param {moment} endMoment
     *
     * @returns {Promise}
     */
    saveEmployerSelectedPeriod(employerId, startMoment, endMoment) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.saveEmployerSelectedPeriod);
        }

        return EzPromise.promise(
            (resolve, reject) => {
                startMoment = EzObject.assignOrDefault(
                    startMoment,
                    EzOptionsService.ezInstance.ezDefaultPeriod.startMoment);

                endMoment = EzObject.assignOrDefault(
                    endMoment,
                    EzOptionsService.ezInstance.ezDefaultPeriod.endMoment);

                EzOptionsService.ezInstance.ezEmployerOptions[EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD] = {
                    periodStart: startMoment,
                    periodEnd: endMoment,
                    startIso: globalThis.ezApi.ezclocker.ezDateTime.ezMomentToIsoString(startMoment),
                    endIso: globalThis.ezApi.ezclocker.ezDateTime.ezMomentToIsoString(endMoment),
                    startMoment,
                    endMoment
                };

                EzOptionsService.ezInstance.ezPersistEmployerOption(
                    employerId,
                    EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_PERIOD,
                    EzJson.toJson({
                        startDateIso8601: globalThis.ezApi.ezclocker.ezDateTime.ezToIso(startMoment),
                        endDateIso8601: globalThis.ezApi.ezclocker.ezDateTime.ezToIso(endMoment)
                    }))
                    .then(
                        resolve,
                        reject);
            });
    }

    /**
     * @public @method
     * Saves the employer's last selected employee
     *
     * @param {Number} employerId
     * @param {Number} employeeId
     *
     * @returns {Promise}
     */
    saveEmployerSelectedEmployee(employerId, employeeId) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.saveEmployerSelectedEmployee);
        }
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.saveEmployerSelectedEmployee);
        }

        return EzOptionsService.ezInstance.ezPersistEmployerOption(
            employerId,
            EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_EMPLOYEE_ID,
            employeeId);
    }

    /**
     * @public @method
     * Returns the employer's last selected employee option
     *
     * @param {number} employerId
     *
     * @returns {Promise}
     *
     * @deprecated Migrate to globalThis.ezApi.ezclocker.ezOptionsService.ezReadEmployerSelectedEmployee()
     */
    readEmployerSelectedEmployee(employerId) {
        return EzOptionsService.ezInstance.ezReadEmployerSelectedEmployee(employerId);
    }

    /**
     * @public @method
     * Returns the employer's last selected employee option
     *
     * @param {number} employerId
     *
     * @returns {Promise.resolve}
     */
    ezReadEmployerSelectedEmployee(employerId) {
        return EzPromise.promise(
            (resolve) => {
                EzOptionsService.ezInstance.ezReadEmployerOption(employerId, EzEmployerOption.EZOPTION_EMPLOYER_SELECTED_EMPLOYEE_ID)
                    .then(
                        (optionResult) => {
                            const value = optionResult.employerOption.optionValue;

                            return EzString.hasLength(value)
                                ? resolve(value)
                                : resolve(null);
                        },
                        (eResponse) => {
                            globalThis.ezApi.ezclocker.ezLogger.error(
                                EzString.em`
                                    Failed to read the employer selected employee option.
                                    Error: ${EzJson.toJson(eResponse)}`);

                            return resolve(null);
                        });
            });
    }

    /**
     * @public @method
     * Reads the employer option value
     *
     * @param {number} employerId
     * @param {string} optionName
     * @param {string} defaultValue
     *
     * @returns {Promise}
     */
    ezReadEmployerOption(employerId, optionName, defaultValue) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezReadEmployerOption);
        }
        if (!EzString.hasLength(optionName)) {
            throw new EzBadParamException(
                'optionName',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezReadEmployerOption);
        }

        return globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
            globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiUrl(
            EzUrl.build`
                employer/${employerId}
                /options/${EzUrl.encodeURL(optionName)}
                ?defaultValue=${EzUrl.encodeURL(EzOptionsService.ezInstance.ezNormalizeOptionValue(defaultValue))}`))
            .then(
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject);
    }

    /**
     * @deprecated
     * Migrate to EzOptionsService.ezInstance.ezReadEmployerOption()
     *
     * @public @method
     * Reads the employer option value
     *
     * @param {number} employerId
     * @param {string} optionName
     * @param {string} defaultValue
     *
     * @returns {Promise}
     */
    readEmployerOption(employerId, optionName, defaultValue) {
        return EzOptionsService.ezInstance.ezReadEmployerOption(
            employerId,
            optionName,
            defaultValue);
    }

    /**
     * @public @method
     * Reads the employers option list
     *
     * @param {number} employerId
     *
     * @returns {Promise}
     */
    readEmployerOptionList(employerId) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.readEmployerOptionList);
        }

        return EzPromise.promise(
            (resolve, reject) => globalThis.ezApi.ezclocker.ezHttpHelper.ezGet(
                globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiUrl(`employer/${employerId}/options`))
                .then(
                    globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                    globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
                .then(
                    (response) => {
                        if (EzArray.hasLength(response.entities)) {
                            return resolve(response);
                        }

                        response.entities.forEach(
                            (option) => {
                                if (EzObject.isValid(option) && EzString.hasLength(option.optionName)) {
                                    EzOptionsService.ezInstance.ezEmployerOptions[option.optionName] =
                                        EzOptionsService.ezInstance.ezNormalizeOptionValue(option.optionValue);
                                }
                            });

                        return resolve(response.entities);
                    },
                    (eResponse) => {
                        globalThis.ezApi.ezclocker.ezLogger.error(
                            `Failed to read the employer option list. Error: ${EzJson.toJson(eResponse)}`);

                        return reject(eResponse);
                    }));
    }

    /**
     * @public @method
     * Saves the employer's option list
     *
     * @param {Number} employerId
     * @param {Array} optionList
     *
     * @returns {Promise}
     */
    saveEmployerOptionList(employerId, optionList) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.saveEmployerOptionList);
        }
        if (!EzArray.hasLength(optionList)) {
            throw new EzBadParamException(
                'optionList',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.saveEmployerOptionList);
        }

        return EzPromise.promise(
            (resolve, reject) => globalThis.ezApi.ezclocker.ezHttpHelper.ezPost(
                globalThis.ezApi.ezclocker.ezNavigation.ezGetInternalApiUrl(`employer/${employerId}/options/bulk`),
                EzJson.toJson({
                    employerId,
                    entities: optionList
                }))
                .then(
                    globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiResolve,
                    globalThis.ezApi.ezclocker.ezHttpHelper.ezProcessApiReject)
                .then(
                    (response) => {
                        const eOptions = response.entities;

                        if (EzArray.hasLength(eOptions)) {
                            eOptions.forEach(
                                option => EzOptionsService.ezInstance.ezEmployerOptions[option.optionName] = option.optionValue);
                        }

                        return resolve(response);
                    },
                    (eResponse) => {
                        globalThis.ezApi.ezclocker.ezLogger.error(
                            `Failed to save the employer option list. Error: ${EzJson.toJson(eResponse)}`);

                        return reject(eResponse);
                    }));
    }

    /**
     * @public @method
     * Reads the employee option value
     *
     * @param {number} employerId
     * @param {number} employeeId
     * @param {string} optionName
     * @param {string} defaultValue
     *
     * @returns {Promise}
     *
     * @deprecated Migrate to globalThis.ezApi.ezclocker.ezOptionsService.ezReadEmployeeOption()
     */
    readEmployeeOption(employerId, employeeId, optionName, defaultValue) {
        return EzOptionsService.ezInstance.ezReadEmployeeOption(
            employerId,
            employeeId,
            optionName,
            defaultValue);
    }

    /**
     * @public @method
     * Reads the employee option value
     *
     * @param {number} employerId
     * @param {number} employeeId
     * @param {string} optionName
     * @param {string} defaultValue
     *
     * @returns {Promise}
     */
    ezReadEmployeeOption(employerId, employeeId, optionName, defaultValue) {
        return EzOptionsService.ezInstance.ezReadEmployerOption(
            employerId,
            `${employeeId}_${optionName}`,
            defaultValue);
    }

    /**
     * @public @method
     * Saves the provided employee option value
     *
     * @param {Number} employerId
     * @param {Number} employeeId
     * @param {String} optionName
     * @param {*} optionValue
     *
     * @return {Promise}
     *
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezSaveEmployeeOption()
     */
    saveEmployeeOption(employerId, employeeId, optionName, optionValue) {
        return EzOptionsService.ezInstance.ezSaveEmployeeOption(
            employerId,
            employeeId,
            optionName,
            optionValue);
    }

    /**
     * @public @method
     * Saves the provided employee option value
     *
     * @param {number} employerId
     * @param {number} employeeId
     * @param {string} optionName
     * @param {*} optionValue
     *
     * @return {Promise}
     */
    ezSaveEmployeeOption(employerId, employeeId, optionName, optionValue) {
        if (!EzNumber.isNumber(employerId)) {
            throw new EzBadParamException(
                'employerId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezSaveEmployeeOption);
        }
        if (!EzNumber.isNumber(employeeId)) {
            throw new EzBadParamException(
                'employeeId',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezSaveEmployeeOption);
        }
        if (!EzString.hasLength(optionName)) {
            throw new EzBadParamException(
                'optionName',
                EzOptionsService.ezInstance,
                EzOptionsService.ezInstance.ezSaveEmployeeOption);
        }

        optionValue = EzObject.isValid(optionValue)
            ? optionValue
            : null;

        return EzOptionsService.ezInstance.ezPersistEmployerOption(
            employerId,
            `${employeeId}_${optionName}`,
            optionValue);
    }

    /**
     * @public @method
     * If the provided ezClockerAccountType is PAYROLL_MANAGER, MANAGER, EMPLOYEE< or PERSONAL then the provided ezEmployerOption is returned using template:
     * `${aAccountId.toString()}_${ezEmployerOption}`
     * If the provided ezClockerAccountType is UNKNOWN, ADMIN, SUPPORT, DEVELOPER, EMPLOYER, PAYROLL_MANAGER_AS_EMPLOYER, or MANAGER_AS_EMPLOYER,
     *
     * @param {string} ezClockerAccountType
     * @param {string|number} accountId
     * Id from the Payroll Manager, Manager, Employee, or Personal account.
     * @param {string} ezEmployerOption
     * Default: EzEmployerOption.UNKNOWN
     * A valid enumeration property value from EzEmployerOption
     *
     * @returns {string}
     *
     * @deprecated
     * Migrate to: EzEmployerOption.ezPrependAccountIdToOptionKey(ezClockerAccountType, accountId, ezEmployerOption);
     */
    ezPrependAccountIdToOptionKey(ezClockerAccountType, accountId, ezEmployerOption = EzEmployerOption.UNKNOWN) {
        return EzEmployerOption.ezPrependAccountIdToOptionKey(
            ezClockerAccountType,
            accountId,
            ezEmployerOption);
    }

    /**
     * @public @metho
     * Returns the option key name to use for a given option name. Some accounts support two different sets
     * of options (manager account as the prime example) and need to swtich sets in on the employer dashboard vs
     * an employee dashboard.
     *
     * @param {string} ezClockerAccountType
     * Default: EzClockerAccountType.UNKNOWN
     * A valid enumeration property value from EzClockerAccountType
     * @param {String} ezEmployerOption
     * Default: EzEmployerOption.UNKNOWN
     * A valid enumeration property value from EzEmployerOption
     *
     * @returns {string}
     * A valid enumeration property value from EzEmployerOption for the provided ezClockerAccountType.
    */
    ezGetActiveAccountOptionKeyNameForAccountType(ezClockerAccountType = EzClockerAccountType.UNKNOWN, ezEmployerOption = EzEmployerOption.UNKNOWN) {
        return EzEmployerOption.ezOptionKeyForAccountType(
            ezEmployerOption,
            ezClockerAccountType);
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezOptionsService';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzOptionsService_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with globalThis.ezApi.
     * @type {EzOptionsService}
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzOptionsService.ezApiName]
        ? globalThis.ezApi.ezclocker[EzOptionsService.ezApiName]
        : null;

    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzOptionsService}
     */
    static get ezInstance() {
        return EzOptionsService.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with globalThis.ezApi.
     * @param {EzOptionsService} instance
     */
    static set ezInstance(instance) {
        if (null != EzOptionsService.#ezInstance) {
            throw new Error('EzOptionsService\'s singleton instance is already reigstered with globalThis.ezApi.');
        }

        EzOptionsService.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     *
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzOptionsService.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     *
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzOptionsService.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     *
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzOptionsService.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * Safe to ignore SonarLint hint javascript:S1541
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzOptionsService.ezApiRegistrationState &&
            globalThis?.ezApi?.ready && globalThis?.ezApi?.ezclocker &&
            globalThis.ezApi.ezclocker?.[EzDateTime.ezApiName]?.ready &&
            globalThis.ezApi.ezclocker?.[EzEventEngine.ezApiName]?.ready &&
            globalThis.ezApi.ezclocker?.[EzHttpHelper.ezApiName]?.ready &&
            globalThis.ezApi.ezclocker?.[EzNavigation.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzOptionsService.ezInstance &&
            EzRegistrationState.REGISTERED === EzOptionsService.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzOptionsService.#ezCanRegister && !EzOptionsService.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzOptionsService, EzOptionsService.ezApiName);
        }

        return EzOptionsService.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzOptionsService.ezApiName
     *     2) Property getter EzOptionsService.ezEventNames
     *     3) Property getter EzOptionsService.ezInstance
     *     4) Property setter EzOptionsService.ezInstance
     *     5) Property getter EzOptionsService.ezApiRegistrationState
     *     6) Property setter EzOptionsService.ezApiRegistrationState
     *     7) Property getter EzOptionsService.#ezCanRegister
     *     8) Property getter EzOptionsService.#ezIsRegistered
     *     9) Method EzOptionsService.#ezRegistrator()
     */
    static {
        if (!EzOptionsService.#ezIsRegistered) {
            EzOptionsService.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzOptionsService.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzOptionsService.ezOnEzApiReadyEventName,
                    EzOptionsService.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzDateTime.ezEventNames.onReady,
                    EzOptionsService.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzEventEngine.ezEventNames.onReady,
                    EzOptionsService.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzHttpHelper.ezEventNames.onReady,
                    EzOptionsService.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzNavigation.ezEventNames.onReady,
                    EzOptionsService.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

}
